'use client';

import React from 'react';

interface MarkdownPreviewProps {
  content: string;
}

function inlineCodeParts(text: string) {
  return text.split(/(`[^`]+`)/g).map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code data-component-id="src/app/components/MarkdownPreview.tsx:13:8" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="13" data-component-end-line="15" data-component-file="MarkdownPreview.tsx" data-component-name="code" data-component-content="%7B%22elementName%22%3A%22code%22%2C%22className%22%3A%22rounded%20bg-muted%20px-1%20py-0.5%20text-2xs%20text-primary%22%7D" key={index} className="rounded bg-muted px-1 py-0.5 text-2xs text-primary">
          {part.slice(1, -1)}
        </code>
      );
    }

    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function isPotentialTableRow(line: string) {
  const trimmed = line.trim();
  return trimmed.includes('|') && trimmed.replace(/\|/g, '').trim().length > 0;
}

function isTableDelimiter(line: string) {
  const cells = splitTableRow(line);

  if (cells.length === 0) {
    return false;
  }

  return cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, '')));
}

function splitTableRow(line: string) {
  const trimmed = line.trim();
  const withoutOuterPipes = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cell = '';
  let isEscaped = false;

  for (const char of withoutOuterPipes) {
    if (char === '|' && !isEscaped) {
      cells.push(cell.trim().replace(/\\\|/g, '|'));
      cell = '';
      continue;
    }

    cell += char;
    isEscaped = char === '\\' && !isEscaped;

    if (char !== '\\') {
      isEscaped = false;
    }
  }

  cells.push(cell.trim().replace(/\\\|/g, '|'));
  return cells;
}

export default function MarkdownPreview({ content }: MarkdownPreviewProps) {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let codeLines: string[] = [];
  let inCode = false;

  const flushList = () => {
    if (listItems.length === 0) {
      return;
    }

    blocks.push(
      <ul data-component-id="src/app/components/MarkdownPreview.tsx:77:6" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="77" data-component-end-line="81" data-component-file="MarkdownPreview.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22list-disc%20space-y-1%20pl-5%20text-sm%20text-foreground%2F85%22%7D" key={`list-${blocks.length}`} className="list-disc space-y-1 pl-5 text-sm text-foreground/85">
        {listItems.map((item, index) => (
          <li data-component-id="src/app/components/MarkdownPreview.tsx:79:10" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="79" data-component-end-line="79" data-component-file="MarkdownPreview.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={`${item}-${index}`}>{inlineCodeParts(item)}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  const flushCode = () => {
    if (codeLines.length === 0) {
      return;
    }

    blocks.push(
      <pre data-component-id="src/app/components/MarkdownPreview.tsx:92:6" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="92" data-component-end-line="97" data-component-file="MarkdownPreview.tsx" data-component-name="pre" data-component-content="%7B%22elementName%22%3A%22pre%22%2C%22className%22%3A%22overflow-x-auto%20rounded-lg%20border%20border-border%20bg-background%2F80%20p-3%20text-xs%20text-foreground%22%7D"
        key={`code-${blocks.length}`}
        className="overflow-x-auto rounded-lg border border-border bg-background/80 p-3 text-xs text-foreground"
      >
        <code data-component-id="src/app/components/MarkdownPreview.tsx:96:8" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="96" data-component-end-line="96" data-component-file="MarkdownPreview.tsx" data-component-name="code" data-component-content="%7B%22elementName%22%3A%22code%22%7D">{codeLines.join('\n')}</code>
      </pre>
    );
    codeLines = [];
  };

  const renderTable = (headerLine: string, bodyLines: string[]) => {
    const headerCells = splitTableRow(headerLine);
    const bodyRows = bodyLines.map((row) => splitTableRow(row));

    blocks.push(
      <div data-component-id="src/app/components/MarkdownPreview.tsx:107:6" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="107" data-component-end-line="134" data-component-file="MarkdownPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-x-auto%20rounded-lg%20border%20border-border%22%7D" key={`table-${blocks.length}`} className="overflow-x-auto rounded-lg border border-border">
        <table data-component-id="src/app/components/MarkdownPreview.tsx:108:8" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="108" data-component-end-line="133" data-component-file="MarkdownPreview.tsx" data-component-name="table" data-component-content="%7B%22elementName%22%3A%22table%22%2C%22className%22%3A%22min-w-full%20border-collapse%20text-left%20text-sm%22%7D" className="min-w-full border-collapse text-left text-sm">
          <thead data-component-id="src/app/components/MarkdownPreview.tsx:109:10" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="109" data-component-end-line="121" data-component-file="MarkdownPreview.tsx" data-component-name="thead" data-component-content="%7B%22elementName%22%3A%22thead%22%2C%22className%22%3A%22bg-muted%2F60%20text-foreground%22%7D" className="bg-muted/60 text-foreground">
            <tr data-component-id="src/app/components/MarkdownPreview.tsx:110:12" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="110" data-component-end-line="120" data-component-file="MarkdownPreview.tsx" data-component-name="tr" data-component-content="%7B%22elementName%22%3A%22tr%22%7D">
              {headerCells.map((cell, index) => (
                <th data-component-id="src/app/components/MarkdownPreview.tsx:112:16" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="112" data-component-end-line="118" data-component-file="MarkdownPreview.tsx" data-component-name="th" data-component-content="%7B%22elementName%22%3A%22th%22%2C%22className%22%3A%22border-b%20border-border%20px-3%20py-2%20font-semibold%22%7D"
                  key={`${cell}-${index}`}
                  scope="col"
                  className="border-b border-border px-3 py-2 font-semibold"
                >
                  {inlineCodeParts(cell)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-component-id="src/app/components/MarkdownPreview.tsx:122:10" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="122" data-component-end-line="132" data-component-file="MarkdownPreview.tsx" data-component-name="tbody" data-component-content="%7B%22elementName%22%3A%22tbody%22%2C%22className%22%3A%22divide-y%20divide-border%20text-foreground%2F85%22%7D" className="divide-y divide-border text-foreground/85">
            {bodyRows.map((row, rowIndex) => (
              <tr data-component-id="src/app/components/MarkdownPreview.tsx:124:14" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="124" data-component-end-line="130" data-component-file="MarkdownPreview.tsx" data-component-name="tr" data-component-content="%7B%22elementName%22%3A%22tr%22%2C%22className%22%3A%22bg-background%2F60%22%7D" key={`row-${rowIndex}`} className="bg-background/60">
                {Array.from({ length: headerCells.length }).map((_, cellIndex) => (
                  <td data-component-id="src/app/components/MarkdownPreview.tsx:126:18" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="126" data-component-end-line="128" data-component-file="MarkdownPreview.tsx" data-component-name="td" data-component-content="%7B%22elementName%22%3A%22td%22%2C%22className%22%3A%22px-3%20py-2%20align-top%22%7D" key={`cell-${rowIndex}-${cellIndex}`} className="px-3 py-2 align-top">
                    {inlineCodeParts(row[cellIndex] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];

    if (line.trim().startsWith('```')) {
      if (inCode) {
        inCode = false;
        flushCode();
      } else {
        flushList();
        inCode = true;
      }
      continue;
    }

    if (inCode) {
      codeLines.push(line);
      continue;
    }

    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.+)$/.exec(trimmed);
    if (heading) {
      flushList();
      const level = heading[1].length;
      const className =
        level === 1
          ? 'text-xl font-semibold text-foreground'
          : level === 2
            ? 'text-lg font-semibold text-foreground'
            : 'text-sm font-semibold uppercase tracking-widest text-muted-foreground';
      blocks.push(
        <div data-component-id="src/app/components/MarkdownPreview.tsx:175:8" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="175" data-component-end-line="177" data-component-file="MarkdownPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" key={`heading-${blocks.length}`} className={className}>
          {heading[2]}
        </div>
      );
      continue;
    }

    const bullet = /^[-*]\s+(.+)$/.exec(trimmed);
    if (bullet) {
      listItems.push(bullet[1]);
      continue;
    }

    if (
      isPotentialTableRow(trimmed) &&
      lineIndex + 1 < lines.length &&
      isTableDelimiter(lines[lineIndex + 1])
    ) {
      const bodyLines: string[] = [];
      let nextLineIndex = lineIndex + 2;

      while (nextLineIndex < lines.length && isPotentialTableRow(lines[nextLineIndex])) {
        bodyLines.push(lines[nextLineIndex]);
        nextLineIndex += 1;
      }

      flushList();
      renderTable(trimmed, bodyLines);
      lineIndex = nextLineIndex - 1;
      continue;
    }

    flushList();
    blocks.push(
      <p data-component-id="src/app/components/MarkdownPreview.tsx:209:6" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="209" data-component-end-line="211" data-component-file="MarkdownPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-relaxed%20text-foreground%2F85%22%7D" key={`p-${blocks.length}`} className="text-sm leading-relaxed text-foreground/85">
        {inlineCodeParts(trimmed)}
      </p>
    );
  }

  flushList();
  flushCode();

  if (blocks.length === 0) {
    return <p data-component-id="src/app/components/MarkdownPreview.tsx:219:11" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="219" data-component-end-line="219" data-component-file="MarkdownPreview.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22No%20Markdown%20content%20to%20preview.%22%7D" className="text-sm text-muted-foreground">No Markdown content to preview.</p>;
  }

  return <div data-component-id="src/app/components/MarkdownPreview.tsx:222:9" data-component-path="src/app/components/MarkdownPreview.tsx" data-component-line="222" data-component-end-line="222" data-component-file="MarkdownPreview.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">{blocks}</div>;
}
